import React, { useState } from "react";
import {
  Eye,
  Tag,
  Clock,
  AlertTriangle,
  Shield,
  MoreVertical,
  User,
  Mail,
  Hash,
  ShieldCheck,
} from "lucide-react";
import { Customer } from "../../types/customer";
import { CustomerDetailsDrawer } from "../customers/customerDetail/CustomerDetailsDrawer";
import { useUnbanCustomer } from "../../hooks/useBanRequests";
import { UnbanConfirmationModal } from "./UnbanConfirmationModel";
import { Pagination } from "../common/Pagination";

interface AllCustomersTableProps {
  customers: Customer[];
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const AllCustomersTable: React.FC<AllCustomersTableProps> = ({
  customers,
  currentPage,
  itemsPerPage,
  totalPages,
  onPageChange,
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [unbanModalOpen, setUnbanModalOpen] = useState<Customer | null>(null);

  const unbanMutation = useUnbanCustomer();

  const getBanStatusInfo = (status: string) => {
    switch (status) {
      case "under_monitoring":
        return {
          icon: <Tag className="w-4 h-4 text-yellow-400" />,
          label: "Under Monitoring",
          color: "text-yellow-400",
          bgColor: "bg-yellow-400/10",
        };
      case "temporary_ban":
        return {
          icon: <Clock className="w-4 h-4 text-orange-400" />,
          label: "Temporarily Banned",
          color: "text-orange-400",
          bgColor: "bg-orange-400/10",
        };
      case "permanently_banned":
        return {
          icon: <AlertTriangle className="w-4 h-4 text-red-400" />,
          label: "Permanently Banned",
          color: "text-red-400",
          bgColor: "bg-red-400/10",
        };
      case "active":
        return {
          icon: <Shield className="w-4 h-4 text-green-400" />,
          label: "Active",
          color: "text-green-400",
          bgColor: "bg-green-400/10",
        };
      default:
        return {
          icon: <Shield className="w-4 h-4 text-slate-400" />,
          label: status || "Unknown",
          color: "text-slate-400",
          bgColor: "bg-slate-400/10",
        };
    }
  };

  const getCustomerTypeColor = (type: string) => {
    switch (type) {
      case "vip":
        return "bg-yellow-600 text-yellow-100";
      case "vvip":
        return "bg-purple-600 text-purple-100";
      default:
        return "bg-slate-600 text-slate-100";
    }
  };

  const getCustomerTypeLabel = (type: string) => {
    switch (type) {
      case "vip":
        return "VIP";
      case "vvip":
        return "VVIP";
      default:
        return "Regular";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
    setActionMenuOpen(null);
  };

  const handleUnbanClick = (customer: Customer) => {
    setUnbanModalOpen(customer);
    setActionMenuOpen(null);
  };

  const handleUnbanConfirm = async (customerId: string) => {
    try {
      await unbanMutation.mutateAsync(customerId);
      setUnbanModalOpen(null);
    } catch (error) {
      console.error("Failed to unban customer:", error);
    }
  };

  const getImageSrc = (photo: string | undefined, customerId: string) => {
    if (imageErrors.has(customerId)) {
      return "/default-avatar.png";
    }

    if (!photo) return "/default-avatar.png";
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    return photo.startsWith("/") ? `${baseUrl}${photo}` : `${baseUrl}/${photo}`;
  };

  const handleImageError = (customerId: string) => {
    setImageErrors((prev) => {
      const newSet = new Set(prev);
      newSet.add(customerId);
      return newSet;
    });
  };

  const canUnbanCustomer = (customer: Customer) => {
    return customer.banStatus !== "active";
  };

  const getDropdownPosition = (index: number) => {
    const isNearBottom = index >= customers.length - 2;
    return isNearBottom ? "bottom-10" : "top-10";
  };

  if (customers.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        <User className="w-12 h-12 mx-auto mb-4 text-slate-500" />
        <h3 className="text-lg font-medium text-slate-300 mb-2">
          No Customers Found
        </h3>
        <p>There are no customers to display at the moment.</p>
      </div>
    );
  }

  return (
    <>
      {/* Table Container with proper overflow handling */}
      <div className="relative">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  S.N.
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Player ID
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Ban Status
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Registration Date
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Ban End Date
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Remarks
                </th>
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {customers.map((customer, index) => {
                const statusInfo = getBanStatusInfo(customer.banStatus);
                const serialNumber =
                  (currentPage - 1) * itemsPerPage + index + 1;

                return (
                  <tr
                    key={customer.id}
                    className="hover:bg-slate-700 transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      {serialNumber}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            src={getImageSrc(customer.photo, customer.id)}
                            alt={customer.name}
                            className="h-10 w-10 rounded-full object-cover border border-slate-600"
                            onError={() => handleImageError(customer.id)}
                          />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-white flex items-center gap-2">
                            <User className="w-3 h-3 text-slate-400" />
                            {customer.name}
                          </div>
                          <div className="text-sm text-slate-400 flex items-center gap-2">
                            <Mail className="w-3 h-3" />
                            {customer.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Hash className="w-3 h-3 text-slate-400" />
                        <span className="text-sm font-mono text-white">
                          {customer.playerId}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(customer.customerType)}`}
                      >
                        {getCustomerTypeLabel(customer.customerType)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${statusInfo.bgColor}`}
                      >
                        {statusInfo.icon}
                        <span
                          className={`text-sm font-medium ${statusInfo.color}`}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      {formatDate(customer.registrationDate)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      {customer.banStatus === "temporary_ban" &&
                      customer.banEndDate ? (
                        <div>
                          <div className="text-white">
                            {formatDate(customer.banEndDate)}
                          </div>
                          <div className="text-xs text-slate-400">
                            {new Date(customer.banEndDate) > new Date()
                              ? "Active"
                              : "Expired"}
                          </div>
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-4 py-4 max-w-xs">
                      <div
                        className="text-sm text-slate-300 truncate"
                        title={customer.banRemarks || ""}
                      >
                        {customer.banRemarks || "No remarks"}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                      <div className="relative">
                        <button
                          onClick={() =>
                            setActionMenuOpen(
                              actionMenuOpen === customer.id
                                ? null
                                : customer.id,
                            )
                          }
                          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-600 transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {actionMenuOpen === customer.id && (
                          <div
                            className={`absolute right-0 ${getDropdownPosition(index)} mt-1 w-48 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50`}
                            style={{
                              transform:
                                getDropdownPosition(index) === "bottom-10"
                                  ? "translateY(-100%)"
                                  : "none",
                            }}
                          >
                            <div className="py-1">
                              <button
                                onClick={() => handleViewDetails(customer)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-600"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              {canUnbanCustomer(customer) && (
                                <button
                                  onClick={() => handleUnbanClick(customer)}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-600"
                                >
                                  <ShieldCheck className="w-4 h-4 text-green-400" />
                                  Unban Customer
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Close dropdown when clicking outside */}
        {actionMenuOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActionMenuOpen(null)}
          />
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-700">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}

      {/* Customer Details Drawer */}
      {selectedCustomer && (
        <CustomerDetailsDrawer
          customer={selectedCustomer}
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedCustomer(null);
          }}
        />
      )}

      {/* Unban Confirmation Modal */}
      <UnbanConfirmationModal
        customer={unbanModalOpen}
        isOpen={!!unbanModalOpen}
        onClose={() => setUnbanModalOpen(null)}
        onConfirm={handleUnbanConfirm}
        isLoading={unbanMutation.isPending}
      />
    </>
  );
};
